import type { SubmitPlatformFeedbackInput } from '~/graphql/generated/graphql';
import type { SchoolsFeedbackCategory, SchoolsFeedbackSubmission } from '~/types/Schools/SchoolsFeedback';

import { EVENTS } from '~/constants/events';
import { SUBMIT_PLATFORM_FEEDBACK } from '~/graphql/documents/userFeedback';
import { FeedbackCategory } from '~/graphql/generated/graphql';

const CATEGORY_MAP: Record<SchoolsFeedbackCategory, FeedbackCategory> = {
  feature_suggestion: FeedbackCategory.FeatureSuggestion,
  bug_report: FeedbackCategory.BugReport,
  lesson_request: FeedbackCategory.LessonRequest,
  general_feedback: FeedbackCategory.GeneralFeedback,
};

/**
 * Platform-agnostic feedback submission. Takes a ready-made input and runs the mutation.
 * Each platform wraps this with its own role/context resolution (see `useSchoolsFeedback` below).
 */
export const usePlatformFeedback = () => {
  const { $urqlClient, $logErrorMessage } = useNuxtApp();
  const submitting = ref(false);

  const submitPlatformFeedback = async (input: SubmitPlatformFeedbackInput) => {
    submitting.value = true;

    try {
      const { data, error } = await $urqlClient.mutation(SUBMIT_PLATFORM_FEEDBACK, { input });

      if (error) {
        $logErrorMessage('Failed to submit platform feedback', { error });
        return undefined;
      }

      return data?.core?.userFeedback?.submitPlatformFeedback;
    }
    catch (error) {
      $logErrorMessage('Failed to submit platform feedback', { error });
      return undefined;
    }
    finally {
      submitting.value = false;
    }
  };

  return { submitPlatformFeedback, submitting: readonly(submitting) };
};

export const useSchoolsFeedback = () => {
  const { $logErrorMessage } = useNuxtApp();
  const route = useRoute();
  const analytics = useAnalyticsService();
  const { currentSchoolId, currentSchoolRole, accountTeacherRole } = useAuth();
  const { submitPlatformFeedback, submitting } = usePlatformFeedback();

  const currentRoleId = computed(() => currentSchoolRole.value?.id ?? accountTeacherRole.value?.id);

  const submitSchoolsFeedback = async (submission: SchoolsFeedbackSubmission) => {
    const roleId = currentRoleId.value;

    if (!roleId) {
      $logErrorMessage('Cannot submit schools feedback without a teacher role');
      return undefined;
    }

    const result = await submitPlatformFeedback({
      category: CATEGORY_MAP[submission.category],
      comment: submission.comment,
      followUpConsent: submission.followUpConsent,
      roleId,
      pageUrl: route.fullPath,
    });

    if (result) {
      analytics.track(EVENTS.SCHOOLS_FEEDBACK_SUBMITTED, {
        category: submission.category,
        schoolId: currentSchoolId.value,
        roleId: accountTeacherRole.value?.id,
      });
    }

    return result;
  };

  return { submitSchoolsFeedback, submitting };
};
